import { useMemo } from 'react';
import type { ClientCountMetrics } from 'services/types/relifDashboard';
import { formatSpanishMonthForDisplay, parseSpanishMonthKey } from 'utils/dateHelpers';

import {
  calculateTotalLineData,
  filterAndSortMonths,
  formatMonthLabels,
  getMonthsWithData,
  transformIndustryDataForChart,
} from '../utils/chartDataUtils';

interface UseClientsDataReturn {
  monthlyClientCountData: Array<{ label: string; value: number | undefined; monthKey: string; hasData: boolean }>;
  clientCountByIndustryData: Array<{ industry: string; data: number[]; months: string[] }>;
  totalLineData: { industry: string; data: number[]; months: string[] } | null;
  monthLabels: string[];
  monthOverMonthGrowth: number;
  lastAvailableMonth: string | null;
}

export default function useClientsData(
  clientData: ClientCountMetrics | undefined,
  selectedYear: number
): UseClientsDataReturn {
  return useMemo(() => {
    if (!clientData) {
      return {
        monthlyClientCountData: [],
        clientCountByIndustryData: [],
        totalLineData: null,
        monthLabels: [],
        monthOverMonthGrowth: 0,
        lastAvailableMonth: null,
      };
    }

    // Get all months from client data
    const allMonths = new Set<string>();
    if (clientData.clientCountByIndustry) {
      Object.values(clientData.clientCountByIndustry).forEach((monthlyData) => {
        Object.keys(monthlyData).forEach((month) => {
          allMonths.add(month);
        });
      });
    }
    if (clientData.monthlyClientCount) {
      Object.keys(clientData.monthlyClientCount).forEach((month) => {
        allMonths.add(month);
      });
    }

    // Filter and sort months; skip future check so all months from n8n data are shown
    const sortedMonths = Array.from(allMonths).sort((a, b) => parseSpanishMonthKey(a) - parseSpanishMonthKey(b));
    const filteredMonths = filterAndSortMonths(sortedMonths, selectedYear, false, true);

    // Transform monthly client count data
    const monthlyClientCountData = clientData.monthlyClientCount
      ? filteredMonths
          .map((monthKey) => {
            const value = clientData.monthlyClientCount[monthKey];
            return {
              label: formatSpanishMonthForDisplay(monthKey),
              value: typeof value === 'number' ? value : undefined,
              monthKey,
              hasData: typeof value === 'number' && value > 0,
            };
          })
          .filter((d) => d.hasData)
      : [];

    // Get months with data
    const sortedMonthsWithData = getMonthsWithData(clientData.clientCountByIndustry, filteredMonths);

    // Transform industry data for line chart
    const clientCountByIndustryData = transformIndustryDataForChart(
      clientData.clientCountByIndustry,
      sortedMonthsWithData,
      true
    );

    // Calculate total line data
    const totalLineData = calculateTotalLineData(clientCountByIndustryData, sortedMonthsWithData);

    // Format month labels
    const monthLabels = formatMonthLabels(
      clientCountByIndustryData.length > 0 || totalLineData
        ? (clientCountByIndustryData[0] || totalLineData)?.months || []
        : []
    );

    // Calculate month-over-month growth
    const monthsWithDataForGrowth = filteredMonths
      .filter((month) => {
        const value = clientData.monthlyClientCount?.[month];
        return typeof value === 'number' && value > 0;
      })
      .sort((a, b) => parseSpanishMonthKey(a) - parseSpanishMonthKey(b));

    const lastAvailableMonth =
      monthsWithDataForGrowth.length > 0 ? monthsWithDataForGrowth[monthsWithDataForGrowth.length - 1] : null;

    const rawMonthOverMonthGrowth =
      monthsWithDataForGrowth.length >= 2
        ? (() => {
            const lastMonth = monthsWithDataForGrowth[monthsWithDataForGrowth.length - 1];
            const previousMonth = monthsWithDataForGrowth[monthsWithDataForGrowth.length - 2];

            const currentMonthCount = clientData.monthlyClientCount?.[lastMonth] ?? 0;
            const previousMonthCount = clientData.monthlyClientCount?.[previousMonth] ?? 0;

            if (previousMonthCount > 0 && Number.isFinite(previousMonthCount)) {
              return ((currentMonthCount - previousMonthCount) / previousMonthCount) * 100;
            }
            return 0;
          })()
        : 0;

    // Cap growth for display to avoid extreme values (e.g. -6667% from tiny or inconsistent data)
    let monthOverMonthGrowth: number;
    if (Number.isFinite(rawMonthOverMonthGrowth) && Math.abs(rawMonthOverMonthGrowth) <= 1000) {
      monthOverMonthGrowth = rawMonthOverMonthGrowth;
    } else if (rawMonthOverMonthGrowth < 0) {
      monthOverMonthGrowth = -100;
    } else {
      monthOverMonthGrowth = 100;
    }

    return {
      monthlyClientCountData,
      clientCountByIndustryData,
      totalLineData,
      monthLabels,
      monthOverMonthGrowth,
      lastAvailableMonth,
    };
  }, [clientData, selectedYear]);
}
